import React, { useState, useMemo } from 'react'
import { designTokens } from '@/utils/design-tokens'

interface PerformanceItem {
  id: string
  name: string
  target: number | string | null
  achievement: number | string | null
}

interface PerformanceData {
  performanceName: string
  unit: string
  items: PerformanceItem[]
}

interface PerformanceSectionProps {
  performanceData: PerformanceData
  onChange: (performanceData: PerformanceData) => void
  disabled?: boolean
}

const PerformanceSection: React.FC<PerformanceSectionProps> = ({
  performanceData,
  onChange,
  disabled = false
}) => {
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [editingValues, setEditingValues] = useState<Record<string, string>>({})

  // Calculate totals (supports decimal numbers)
  const totals = useMemo(() => {
    const totalTarget = performanceData.items.reduce((sum, item) => {
      const target = typeof item.target === 'string' ? parseFloat(item.target) || 0 : (item.target || 0)
      return sum + target
    }, 0)
    const totalAchievement = performanceData.items.reduce((sum, item) => {
      const achievement = typeof item.achievement === 'string' ? parseFloat(item.achievement) || 0 : (item.achievement || 0)
      return sum + achievement
    }, 0)
    const achievementRate = totalTarget > 0 ? Math.round((totalAchievement / totalTarget) * 10000) / 100 : 0

    return {
      target: totalTarget,
      achievement: totalAchievement,
      rate: achievementRate
    }
  }, [performanceData.items])

  // Format number with commas (supports decimal numbers)
  const formatNumber = (num: number | string) => {
    const numValue = typeof num === 'string' ? parseFloat(num) || 0 : num
    return new Intl.NumberFormat('ko-KR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(numValue)
  }

  // Handle performance name change
  const handlePerformanceNameChange = (performanceName: string) => {
    onChange({
      ...performanceData,
      performanceName
    })
  }

  // Handle unit change
  const handleUnitChange = (unit: string) => {
    onChange({
      ...performanceData,
      unit
    })
  }

  // Handle clear performance name
  const handleClearPerformanceName = () => {
    onChange({
      ...performanceData,
      performanceName: ''
    })
  }

  // Handle clear unit
  const handleClearUnit = () => {
    onChange({
      ...performanceData,
      unit: ''
    })
  }

  // Handle adding new KPI item
  const handleAddItem = () => {
    const newItem: PerformanceItem = {
      id: `new-performance-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: '',
      target: null,
      achievement: null
    }
    onChange({
      ...performanceData,
      items: [...performanceData.items, newItem]
    })
  }

  // Handle removing KPI item
  const handleRemoveItem = (itemId: string) => {
    onChange({
      ...performanceData,
      items: performanceData.items.filter(item => item.id !== itemId)
    })
    // Clear any errors for removed item
    const newErrors = { ...errors }
    Object.keys(newErrors).forEach(key => {
      if (key.includes(itemId)) {
        delete newErrors[key]
      }
    })
    setErrors(newErrors)
  }

  // Handle item field changes
  const handleItemChange = (itemId: string, field: keyof PerformanceItem, value: string | number | null) => {
    const updatedItems = performanceData.items.map(item => 
      item.id === itemId ? { ...item, [field]: value } : item
    )
    onChange({
      ...performanceData,
      items: updatedItems
    })

    // Clear field-specific error
    const errorKey = `${itemId}_${field}`
    if (errors[errorKey]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[errorKey]
        return newErrors
      })
    }
  }

  // Handle number input parsing (supports decimal numbers)
  const parseNumberInput = (value: string): number | null => {
    const cleanValue = value.replace(/,/g, '').trim()
    if (!cleanValue) return null
    const num = parseFloat(cleanValue)
    return isNaN(num) ? null : num
  }

  // Handle input focus for decimal point entry
  const handleInputFocus = (itemId: string, field: 'target' | 'achievement', actualValue: number | string | null) => {
    const editingKey = `${itemId}_${field}`
    const rawValue = actualValue ? String(actualValue) : ''
    setEditingValues(prev => ({ ...prev, [editingKey]: rawValue }))
  }

  // Handle input blur for formatting
  const handleInputBlur = (itemId: string, field: 'target' | 'achievement') => {
    const editingKey = `${itemId}_${field}`
    setEditingValues(prev => {
      const newValues = { ...prev }
      delete newValues[editingKey]
      return newValues
    })
  }

  // Get display value for input
  const getDisplayValue = (itemId: string, field: 'target' | 'achievement', actualValue: number | string | null) => {
    const editingKey = `${itemId}_${field}`
    if (editingValues[editingKey] !== undefined) {
      return editingValues[editingKey]
    }
    return actualValue ? formatNumber(actualValue) : ''
  }

  return (
    <div className="space-y-2">
      {/* Section Header */}
      <div 
        className="pb-2 border-b" 
        style={{ 
          borderBottomColor: '#EBEBEB',
          borderBottomWidth: '1px',
          padding: '7px 0px'
        }}
      >
        <h3 
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '18px',
            fontWeight: 700,
            color: designTokens.colors.gray[6]
          }}
        >
          성과
        </h3>
      </div>

      {/* Content */}
      <div className="space-y-2" style={{ padding: '0px 4px' }}>
        {/* Performance Name */}
        <div className="flex items-center gap-5">
          <div className="w-37 flex-shrink-0">
            <span 
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '16px',
                fontWeight: 400,
                color: designTokens.colors.gray[6]
              }}
            >
              성과명
            </span>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-1.5 px-3 py-0 border rounded" style={{ width: '100%', height: '48px', backgroundColor: disabled ? designTokens.colors.gray[0] : 'white', borderColor: designTokens.colors.gray[1] }}>
              <input
                type="text"
                value={performanceData.performanceName}
                onChange={(e) => handlePerformanceNameChange(e.target.value)}
                placeholder="성과명을 입력해주세요."
                disabled={disabled}
                className="w-full border-none outline-none bg-transparent"
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '16px',
                  color: designTokens.colors.gray[6]
                }}
              />
              {performanceData.performanceName && (
                <button
                  type="button"
                  onClick={handleClearPerformanceName}
                  disabled={disabled}
                  className="flex-shrink-0"
                  style={{ width: '20px', height: '20px' }}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="m6.25 6.25 7.5 7.5m-7.5 0 7.5-7.5" stroke={designTokens.colors.gray[3]} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Unit */}
        <div className="flex items-center gap-5">
          <div className="w-37 flex-shrink-0">
            <span 
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '16px',
                fontWeight: 400,
                color: designTokens.colors.gray[6]
              }}
            >
              단위
            </span>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-1.5 px-3 py-0 border rounded" style={{ width: '100%', height: '48px', backgroundColor: disabled ? designTokens.colors.gray[0] : 'white', borderColor: designTokens.colors.gray[1] }}>
              <input
                type="text"
                value={performanceData.unit}
                onChange={(e) => handleUnitChange(e.target.value)}
                placeholder="단위를 입력하세요 (예: 명, 건, %)"
                disabled={disabled}
                className="w-full border-none outline-none bg-transparent"
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '16px',
                  color: designTokens.colors.gray[6]
                }}
              />
              {performanceData.unit && (
                <button
                  type="button"
                  onClick={handleClearUnit}
                  disabled={disabled}
                  className="flex-shrink-0"
                  style={{ width: '20px', height: '20px' }}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="m6.25 6.25 7.5 7.5m-7.5 0 7.5-7.5" stroke={designTokens.colors.gray[3]} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Performance Summary and Add Button */}
        <div className="space-y-3" style={{ padding: '4px 0px' }}>
          <div className="flex justify-center items-center gap-2.5">
            {/* Total Performance Label */}
            <div className="flex justify-center" style={{ width: '160px' }}>
              <span 
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '16px',
                  fontWeight: 400,
                  color: designTokens.colors.gray[6]
                }}
              >
                목표 성과
              </span>
            </div>

            {/* Performance Summary */}
            <div className="flex justify-center gap-2.5" style={{ flex: 1 }}>
              {/* Target */}
              <div className="flex flex-col justify-center gap-2" style={{ width: '120px' }}>
                <div style={{ width: '120px' }}>
                  <span 
                    style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '14px',
                      fontWeight: 400,
                      color: designTokens.colors.gray[6]
                    }}
                  >
                    목표
                  </span>
                </div>
                <div className="flex items-center p-3" style={{ width: '100%', height: 'auto' }}>
                  <span 
                    style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '16px',
                      fontWeight: 400,
                      color: designTokens.colors.gray[6]
                    }}
                  >
                    {formatNumber(totals.target)}
                  </span>
                </div>
              </div>

              {/* Achievement */}
              <div className="flex flex-col justify-center gap-2" style={{ width: '120px' }}>
                <div style={{ width: '120px' }}>
                  <span 
                    style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '14px',
                      fontWeight: 400,
                      color: designTokens.colors.gray[6]
                    }}
                  >
                    달성
                  </span>
                </div>
                <div className="flex items-center p-3" style={{ width: '100%', height: 'auto' }}>
                  <span 
                    style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '16px',
                      fontWeight: 400,
                      color: designTokens.colors.gray[6]
                    }}
                  >
                    {formatNumber(totals.achievement)}
                  </span>
                </div>
              </div>

              {/* Achievement Rate */}
              <div className="flex flex-col justify-center gap-2" style={{ width: '120px' }}>
                <div style={{ width: '120px' }}>
                  <span 
                    style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '14px',
                      fontWeight: 400,
                      color: designTokens.colors.gray[6]
                    }}
                  >
                    달성율
                  </span>
                </div>
                <div className="flex items-center p-3" style={{ width: '100%', height: 'auto' }}>
                  <span 
                    style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '16px',
                      fontWeight: 400,
                      color: designTokens.colors.gray[6]
                    }}
                  >
                    {totals.rate}%
                  </span>
                </div>
              </div>
            </div>

            {/* Add Button */}
            <div className="flex justify-center items-center gap-1 px-2 py-0" style={{ width: 'auto', height: '28px' }}>
              <button
                type="button"
                onClick={handleAddItem}
                disabled={disabled}
                className="flex items-center gap-1 px-2 py-0 rounded transition-colors"
                style={{
                  backgroundColor: disabled ? designTokens.colors.gray[1] : designTokens.colors.gray[1],
                  color: disabled ? designTokens.colors.gray[3] : designTokens.colors.gray[6],
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '14px',
                  fontWeight: 600,
                  border: `2px solid ${designTokens.colors.gray[1]}`,
                  borderRadius: '4px',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  height: '28px'
                }}
              >
                <span style={{ fontSize: '14px', lineHeight: '14px' }}>+</span>
                KPI 추가
              </button>
            </div>
          </div>

          {/* KPI Items */}
          {performanceData.items.length > 0 && (
            <div className="space-y-1.5">
              {performanceData.items.map((item) => (
                <div key={item.id} className="flex justify-center items-center gap-2.5">
                  {/* Tree Indicator and Performance Name */}
                  <div className="flex items-center gap-1.5 px-0 py-0" style={{ paddingLeft: '10px', width: 'auto' }}>
                    <span 
                      style={{
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        fontSize: '14px',
                        fontWeight: 400,
                        color: designTokens.colors.gray[6]
                      }}
                    >
                      ㄴ
                    </span>
                    <div className="flex flex-col justify-center gap-2" style={{ width: '150px' }}>
                      <div style={{ width: '120px' }}>
                        <div className="flex items-center gap-0.5">
                          <span 
                            style={{
                              fontFamily: designTokens.typography.fontFamily.sans[0],
                              fontSize: '12px',
                              fontWeight: 400,
                              color: designTokens.colors.gray[6]
                            }}
                          >
                            성과명
                          </span>
                          <span style={{ color: designTokens.colors.error.DEFAULT }}>*</span>
                        </div>
                      </div>
                      <div className="flex flex-col justify-center gap-2" style={{ width: '100%' }}>
                        <div className="flex items-center gap-1.5 px-3 py-0 border rounded" style={{ width: '100%', height: '32px', backgroundColor: disabled ? designTokens.colors.gray[0] : 'white', borderColor: errors[`${item.id}_name`] ? designTokens.colors.error.DEFAULT : designTokens.colors.gray[1] }}>
                          <input
                            type="text"
                            value={item.name}
                            onChange={(e) => handleItemChange(item.id, 'name', e.target.value)}
                            placeholder="성과명 입력"
                            disabled={disabled}
                            className="w-full border-none outline-none bg-transparent"
                            style={{
                              fontFamily: designTokens.typography.fontFamily.sans[0],
                              fontSize: '14px',
                              color: designTokens.colors.gray[6]
                            }}
                          />
                        </div>
                        {errors[`${item.id}_name`] && (
                          <span style={{ fontSize: '12px', color: designTokens.colors.error.DEFAULT }}>
                            {errors[`${item.id}_name`]}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Target */}
                  <div className="flex flex-col justify-center gap-2" style={{ width: '120px' }}>
                    <div style={{ width: '120px' }}>
                      <div className="flex items-center gap-0.5">
                        <span 
                          style={{
                            fontFamily: designTokens.typography.fontFamily.sans[0],
                            fontSize: '12px',
                            fontWeight: 400,
                            color: designTokens.colors.gray[6]
                          }}
                        >
                          목표
                        </span>
                        <span style={{ color: designTokens.colors.error.DEFAULT }}>*</span>
                      </div>
                    </div>
                    <div className="flex flex-col justify-center gap-2" style={{ width: '100%' }}>
                      <div className="flex items-center gap-1.5 px-3 py-0 border rounded" style={{ width: '100%', height: '32px', backgroundColor: disabled ? designTokens.colors.gray[0] : 'white', borderColor: errors[`${item.id}_target`] ? designTokens.colors.error.DEFAULT : designTokens.colors.gray[1] }}>
                        <input
                          type="text"
                          value={getDisplayValue(item.id, 'target', item.target)}
                          onChange={(e) => {
                            const editingKey = `${item.id}_target`
                            setEditingValues(prev => ({ ...prev, [editingKey]: e.target.value }))
                            handleItemChange(item.id, 'target', parseNumberInput(e.target.value))
                          }}
                          onFocus={() => handleInputFocus(item.id, 'target', item.target)}
                          onBlur={() => handleInputBlur(item.id, 'target')}
                          placeholder="150"
                          disabled={disabled}
                          className="w-full border-none outline-none bg-transparent"
                          style={{
                            fontFamily: designTokens.typography.fontFamily.sans[0],
                            fontSize: '14px',
                            color: designTokens.colors.gray[6]
                          }}
                        />
                      </div>
                      {errors[`${item.id}_target`] && (
                        <span style={{ fontSize: '12px', color: designTokens.colors.error.DEFAULT }}>
                          {errors[`${item.id}_target`]}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Achievement */}
                  <div className="flex flex-col justify-center gap-2" style={{ width: '120px' }}>
                    <div style={{ width: '120px' }}>
                      <span 
                        style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: '12px',
                          fontWeight: 400,
                          color: designTokens.colors.gray[6]
                        }}
                      >
                        달성
                      </span>
                    </div>
                    <div className="flex flex-col justify-center gap-2" style={{ width: '100%' }}>
                      <div className="flex items-center gap-1.5 px-3 py-0 border rounded" style={{ width: '100%', height: '32px', backgroundColor: disabled ? designTokens.colors.gray[0] : 'white', borderColor: designTokens.colors.gray[1] }}>
                        <input
                          type="text"
                          value={getDisplayValue(item.id, 'achievement', item.achievement)}
                          onChange={(e) => {
                            const editingKey = `${item.id}_achievement`
                            setEditingValues(prev => ({ ...prev, [editingKey]: e.target.value }))
                            handleItemChange(item.id, 'achievement', parseNumberInput(e.target.value))
                          }}
                          onFocus={() => handleInputFocus(item.id, 'achievement', item.achievement)}
                          onBlur={() => handleInputBlur(item.id, 'achievement')}
                          placeholder="50"
                          disabled={disabled}
                          className="w-full border-none outline-none bg-transparent"
                          style={{
                            fontFamily: designTokens.typography.fontFamily.sans[0],
                            fontSize: '14px',
                            color: designTokens.colors.gray[6]
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Delete Button */}
                  <div className="flex flex-col justify-end items-center" style={{ width: 'auto', height: '100%' }}>
                    <div className="flex justify-center items-center gap-1 px-2.5 py-0 border rounded transition-colors" style={{ width: 'auto', height: '34px', backgroundColor: disabled ? designTokens.colors.gray[1] : designTokens.colors.gray[1], borderColor: designTokens.colors.gray[1], cursor: disabled ? 'not-allowed' : 'pointer' }}>
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(item.id)}
                        disabled={disabled}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          color: disabled ? designTokens.colors.gray[3] : designTokens.colors.gray[6],
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: '16px',
                          fontWeight: 600,
                          cursor: disabled ? 'not-allowed' : 'pointer'
                        }}
                      >
                        삭제
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default PerformanceSection